/**
 * Minimal text HUD: timer, speed, mode, attempt status, best time and a
 * transient message line. Text only (textContent), updated at most every
 * animation frame; cheap enough not to show up in the frame budget.
 */
import type { Simulation } from '../simulation/Simulation';

const MESSAGE_MS = 2200;

export function formatTime(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '--:--.---';
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${String(m).padStart(2, '0')}:${s.toFixed(3).padStart(6, '0')}`;
}

/** What the engine needs from a HUD (the developer text HUD below, or the game's). */
export interface HudView {
  say(text: string, nowMs: number): void;
  flash(kind: 'death' | 'finish' | 'checkpoint'): void;
  update(sim: Simulation, nowMs: number, experiments: string): void;
}

export class Hud implements HudView {
  private message = '';
  private messageUntil = 0;
  private lastText = '';

  constructor(
    private readonly el: HTMLElement,
    private readonly flashEl: HTMLElement,
  ) {}

  say(text: string, nowMs: number): void {
    this.message = text;
    this.messageUntil = nowMs + MESSAGE_MS;
  }

  flash(kind: 'death' | 'finish' | 'checkpoint'): void {
    this.flashEl.className = '';
    // Force a style flush so re-adding the class restarts the CSS animation.
    void this.flashEl.offsetWidth;
    this.flashEl.className = `flash-${kind}`;
  }

  update(sim: Simulation, nowMs: number, experiments: string): void {
    const a = sim.attempts;
    const elapsed = a.timer.elapsed(sim.state.tick);
    const status = a.status === 'running' ? '' : ` · ${a.status}`;
    const lines = [
      `${formatTime(elapsed)}`,
      `${sim.state.planarSpeed().toFixed(1)} m/s`,
      `${a.mode === 'full-run' ? 'FULL RUN' : 'PRACTICE'}${status}`,
      `best ${formatTime(a.bestTime())}${experiments ? ` · exp ${experiments}` : ''}`,
    ];
    if (nowMs < this.messageUntil) lines.push(this.message);
    const text = lines.join('\n');
    if (text !== this.lastText) {
      this.el.textContent = text;
      this.lastText = text;
    }
  }
}
